/**
 * Reading a HOVER "Complete Measurements" report.
 *
 * Hover measures the whole house, walls and roof, and it is the report the
 * siding template is measured from (Jonathon, 2026-09-30: "we will use hover
 * report for its measurements"). Its layout is tables rather than sentences:
 * a label at the start of a row, then one figure per column, with lengths
 * written the way a tape reads them — `57' 9"`.
 *
 * The general roof reader (`parseRoofReport`) cannot read it. Hover's roof
 * summary puts a facet *count* before each length — "Valleys - 4 79' 11"" —
 * and a label-then-number scan takes the 4 as the valley. Run on the report
 * for 2805 Saint Patrick Place it read a 20-square roof as 1 square, 4 feet of
 * valley, 5 of flashing and a waste allowance of 1606%. So a Hover report is
 * recognised and read here, by its own labels, and the general reader hands
 * off to this one.
 *
 * Everything is a prefill the rep confirms. A label this does not find costs
 * one number typed by hand; a wrong one would cost a mispriced house, so a
 * doubtful match resolves to nothing.
 */

/** Whether this is a Hover report at all. */
export function isHoverReport(text: string): boolean {
  return (
    /\bHOVER\b/.test(text) && /Complete Measurements|HOVER Inc/i.test(text)
  );
}

/** A square is 100 square feet. Hover prints areas in square feet. */
const SQUARE_FEET = 100;

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * A length as Hover prints it: `102'`, `57' 9"`, or `7"` on its own.
 *
 * Returned in feet to the hundredth. Inches over eleven are not inches, and
 * are refused rather than added.
 */
const LENGTH = String.raw`(?:(\d[\d,]*)'\s*(?:(\d{1,2})")?|(\d{1,2})")`;

function lengthFeet(
  feet: string | undefined,
  inches: string | undefined,
  inchesOnly: string | undefined,
): number | null {
  if (inchesOnly !== undefined) {
    const value = Number(inchesOnly);
    return value >= 0 && value <= 11 ? round2(value / 12) : null;
  }
  if (feet === undefined) return null;
  const whole = Number(feet.replace(/,/g, ""));
  if (!Number.isFinite(whole)) return null;
  let extra = 0;
  if (inches !== undefined) {
    const value = Number(inches);
    if (!Number.isFinite(value) || value < 0 || value > 11) return null;
    extra = value / 12;
  }
  return round2(whole + extra);
}

/** Lines of the report, trimmed, in order. */
const linesOf = (text: string): string[] =>
  text.split(/\r?\n/).map((line) => line.trim());

/**
 * The first length on the first line that starts with `label`.
 *
 * `skip` steps over that many leading columns — Hover's roof summary puts a
 * dash and a count before the length, and the first *length* is still the one
 * wanted, so by default nothing is skipped and the regex simply finds the
 * first tape-measure figure on the row.
 */
function lengthAfter(lines: readonly string[], label: RegExp): number | null {
  for (const line of lines) {
    if (!label.test(line)) continue;
    const rest = line.replace(label, "");
    const match = new RegExp(LENGTH).exec(rest);
    if (!match) return null;
    return lengthFeet(match[1], match[2], match[3]);
  }
  return null;
}

/** The first plain number on the first line that starts with `label`. */
function numberAfter(lines: readonly string[], label: RegExp): number | null {
  for (const line of lines) {
    if (!label.test(line)) continue;
    const rest = line.replace(label, "");
    const match = /(-?\d[\d,]*(?:\.\d+)?)/.exec(rest);
    if (!match) return null;
    const [, raw = ""] = match;
    const value = Number(raw.replace(/,/g, ""));
    return Number.isFinite(value) ? value : null;
  }
  return null;
}

/* -------------------------------------------------------------------------
 * The walls
 * ---------------------------------------------------------------------- */

export interface ParsedWallReport {
  /** Columns of `job_wall_measurements`, by key. */
  values: Record<string, number>;
  /** The keys the report had a number for — what the form highlights. */
  matched: string[];
  /** "Number of Stories", when printed. */
  stories: number | null;
}

/**
 * The wall figures off a Hover report.
 *
 * Every label is read from the start of its row, in the Siding column, which
 * Hover prints first. "Openings" appears twice — once as an area under Areas,
 * once as a table heading with no number after it — and the first row that
 * starts with the word and carries a figure is the area, which is the one
 * wanted. The openings *count* is the "Quantity" row.
 */
export function parseHoverWalls(text: string): ParsedWallReport {
  const lines = linesOf(text);
  const values: Record<string, number> = {};

  const area = (key: string, label: RegExp) => {
    const value = numberAfter(lines, label);
    if (value !== null && value > 0) values[key] = value;
  };
  const length = (key: string, label: RegExp) => {
    const value = lengthAfter(lines, label);
    if (value !== null && value > 0) values[key] = value;
  };
  const count = (key: string, label: RegExp) => {
    const value = numberAfter(lines, label);
    if (value !== null && value > 0) values[key] = Math.round(value);
  };

  area("siding_sqft", /^Facades\b/);
  area("openings_sqft", /^Openings\s+(?=\d)/);
  area("trim_sqft", /^Trims\*?\b/);
  count("openings_qty", /^Quantity\b/);
  length("openings_perimeter_lf", /^Total Perimeter\b/);
  count("inside_corner_qty", /^Inside Qty\b/);
  length("inside_corner_lf", /^Inside Length\b/);
  count("outside_corner_qty", /^Outside Qty\b/);
  length("outside_corner_lf", /^Outside Length\b/);
  count("shutter_qty", /^Shutter Qty\b/);
  count("vent_qty", /^Vents? Qty\b/);
  length("level_starter_lf", /^Level Starter\b/);
  length("sloped_trim_lf", /^Sloped Trim\b/);
  length("vertical_trim_lf", /^Vertical Trim\b/);
  length("eaves_fascia_lf", /^Eaves Fascia\b/);
  length("rakes_fascia_lf", /^Rakes Fascia\b/);
  length("level_frieze_lf", /^Level Frieze Board\b/);
  length("sloped_frieze_lf", /^Sloped Frieze Board\b/);

  /*
   * "Siding & Trim Only", zero waste: the first "Zero Waste" row after that
   * heading. The two tables under it add openings back in, and the rep picks
   * the waste factor on the form, so only the base figure is read.
   */
  const heading = lines.findIndex((line) => /^Siding & Trim Only/i.test(line));
  if (heading !== -1) {
    const value = numberAfter(
      lines.slice(heading + 1, heading + 4),
      /^Zero Waste\b/,
    );
    if (value !== null && value > 0) values.siding_trim_sqft = value;
  }

  // The soffit summary's totals row: a length, then an area.
  const totals = lines.find((line) => /^Totals\b/.test(line));
  if (totals) {
    const rest = totals.replace(/^Totals\b/, "");
    const len = new RegExp(LENGTH).exec(rest);
    if (len) {
      const feet = lengthFeet(len[1], len[2], len[3]);
      if (feet !== null && feet > 0) values.soffit_lf = feet;
      const after = rest.slice((len.index ?? 0) + len[0].length);
      const sqft = /(\d[\d,]*)\s*ft²/.exec(after);
      if (sqft) {
        const [, raw = ""] = sqft;
        const value = Number(raw.replace(/,/g, ""));
        if (Number.isFinite(value) && value > 0) values.soffit_sqft = value;
      }
    }
  }

  const stories = numberAfter(lines, /^Number of Stories\b:?/);

  return {
    values,
    matched: Object.keys(values),
    stories: stories !== null && stories > 0 ? Math.round(stories) : null,
  };
}

/* -------------------------------------------------------------------------
 * The roof
 * ---------------------------------------------------------------------- */

export interface ParsedHoverRoof {
  /** Columns of `job_measurements`, by key; areas in squares. */
  values: Record<string, number>;
  /** The pitch covering the most roof, as "6/12". */
  averagePitch: string | null;
}

/**
 * A roof length, from the coded page or, failing that, the summary.
 *
 * "Eaves   -   8   95' 9"": on the summary the dash is the area column and
 * the 8 the facet count, both stepped over before the length is read.
 */
function roofLength(
  lines: readonly string[],
  coded: RegExp,
  summary: RegExp,
): number | null {
  const value = lengthAfter(lines, coded);
  if (value !== null) return value;
  for (const line of lines) {
    if (!summary.test(line)) continue;
    const rest = line.replace(summary, "").replace(/^\s*-\s*\d+\s*/, "");
    const match = new RegExp(LENGTH).exec(rest);
    return match ? lengthFeet(match[1], match[2], match[3]) : null;
  }
  return null;
}

/**
 * The pitch table, as area per rise. Each row: rise / 12, area, percentage.
 *
 * Hover prints the same table twice — in the roof summary and again on its
 * own page — so a rise already seen is not added a second time.
 */
function pitchTable(lines: readonly string[]): Map<number, number> {
  const byRise = new Map<number, number>();
  for (const line of lines) {
    const match = /^(\d{1,2})\s*\/\s*12\s+(\d[\d,]*)\s*ft²/.exec(line);
    if (!match) continue;
    const [, riseRaw = "", areaRaw = ""] = match;
    const rise = Number(riseRaw);
    const area = Number(areaRaw.replace(/,/g, ""));
    if (!Number.isFinite(area) || area <= 0 || byRise.has(rise)) continue;
    byRise.set(rise, area);
  }
  return byRise;
}

/**
 * The two steep bands the company charges in, in square feet, and the rise
 * covering the most roof. 7/12 and under carry no steep charge.
 */
function steepBands(byRise: ReadonlyMap<number, number>): {
  mid: number;
  high: number;
  predominant: number | null;
} {
  let mid = 0;
  let high = 0;
  let predominant: { rise: number; area: number } | null = null;
  for (const [rise, area] of byRise) {
    if (!predominant || area > predominant.area) predominant = { rise, area };
    if (rise >= 8 && rise <= 12) mid += area;
    else if (rise >= 13 && rise <= 16) high += area;
  }
  return { mid, high, predominant: predominant?.rise ?? null };
}

const ROOF_LENGTHS: readonly [key: string, coded: RegExp, summary: RegExp][] = [
  ["ridge_lf", /^Ridges \(RI\)/, /^Ridges\s+-\s+\d+\s/],
  ["hip_lf", /^Hips \(H\)/, /^Hips\s+-\s+\d+\s/],
  ["valley_lf", /^Valleys \(V\)/, /^Valleys\s+-\s+\d+\s/],
  ["rake_lf", /^Rakes \(RA\)/, /^Rakes\s+-\s+\d+\s/],
  ["eave_lf", /^Eaves \(E\)/, /^Eaves\s+-\s+\d+\s/],
  ["flashing_lf", /^Flashing \(F\)\*?/, /^Flashing\s+-\s+\d+\s/],
  [
    "step_flashing_lf",
    /^Step Flashing \(SF\)\*?/,
    /^Step Flashing\s+-\s+\d+\s/,
  ],
  ["transition_lf", /^Transition Line \(TL\)/, /^Transition Line\s+-\s+\d+\s/],
];

/**
 * The roof figures off a Hover report.
 *
 * Read from the "Roof Measurements" page, where each line carries its code —
 * "Ridges (RI) 72' 6"" — rather than the summary, where a facet count sits
 * before the length. The summary is the fallback, with the dash and the count
 * stepped over explicitly. Hover prints the hip and the ridge apart, so both
 * land in their own columns.
 *
 * The pitch table gives the two steep bands the company charges in and the
 * predominant pitch. Hover recommends no waste figure, so none is read: the
 * rep picks one on the form.
 */
export function parseHoverRoof(text: string): ParsedHoverRoof {
  const lines = linesOf(text);
  const values: Record<string, number> = {};

  const facets = numberAfter(lines, /^Roof Facets\b/);
  if (facets !== null && facets > 0) {
    values.squares = round2(facets / SQUARE_FEET);
  }

  for (const [key, coded, summary] of ROOF_LENGTHS) {
    const value = roofLength(lines, coded, summary);
    if (value !== null && value > 0) values[key] = value;
  }

  const pitches = steepBands(pitchTable(lines));
  if (pitches.mid > 0)
    values.steep_mid_squares = round2(pitches.mid / SQUARE_FEET);
  if (pitches.high > 0) {
    values.steep_high_squares = round2(pitches.high / SQUARE_FEET);
  }

  return {
    values,
    averagePitch: pitches.predominant ? `${pitches.predominant}/12` : null,
  };
}
